import React, { useRef, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Mail, MessageSquare } from "lucide-react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useNavigate } from "react-router-dom";
import { submitContactForm, ContactFormPayload } from "@/services/contactService";
import { DEV_MODE, getCaptchaToken, getCaptchaHeaders, handleDevModeEmailSubmit } from "@/utils/devMode";
import { apiService } from '@/services/ApiService';

// Add Turnstile type declaration
declare global {
  interface Window {
    turnstile: {
      render: (container: string | HTMLElement, options: any) => string;
      reset: (widgetId: string) => void;
    };
  }
}

const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters." }),
  email: z.string().email({ message: "Please enter a valid email address." }),
  phone: z.string().optional(),
  message: z.string().min(10, { message: "Message must be at least 10 characters." }),
  website: z.string().max(0, { message: "Invalid submission." }),
});

type FormValues = z.infer<typeof formSchema>;

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

const Contact = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [turnstileToken, setTurnstileToken] = React.useState<string | null>(null);
  const [turnstileError, setTurnstileError] = React.useState<string | null>(null);
  const turnstileWidgetId = useRef<string | null>(null);

  React.useEffect(() => {
    // Only load Turnstile script if not in dev mode
    if (!DEV_MODE.isEnabled) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);

      return () => {
        document.head.removeChild(script);
      };
    }
  }, []);

  React.useEffect(() => {
    // Only render Turnstile widget if not in dev mode
    if (!DEV_MODE.isEnabled && window.turnstile && TURNSTILE_SITE_KEY) {
      turnstileWidgetId.current = window.turnstile.render('#turnstile-container', {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => {
          console.log('Turnstile token received');
          setTurnstileToken(token);
          setTurnstileError(null);
        },
        'expired-callback': () => {
          console.log('Turnstile token expired');
          setTurnstileToken(null);
          setTurnstileError("Verification expired. Please try again.");
        },
        'error-callback': () => {
          console.error('Turnstile error');
          setTurnstileToken(null);
          setTurnstileError("Verification failed. Please try again.");
        }
      });
    }
  }, [window.turnstile]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
      website: "", // Honeypot field
    },
  });

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function onSubmit(data: FormValues) {
    console.log('Form submitted with data:', data);
    console.log('Form validation state:', form.formState);
    
    if (data.website) {
      console.log('Honeypot field filled, rejecting submission');
      return;
    }

    if (!DEV_MODE.isEnabled && !turnstileToken) {
      setTurnstileError("Please complete the verification challenge.");
      return;
    }

    setLoading(true);
    setError(null);

    // Handle dev mode submission
    const devModeResult = await handleDevModeEmailSubmit("contact", data, {
      onSuccess: () => {
        toast({
          title: "Message Sent",
          description: "Thank you for contacting us. We'll get back to you soon!",
        });
        form.reset();
        setTurnstileToken(null);
        if (turnstileWidgetId.current && !DEV_MODE.isEnabled) {
          window.turnstile.reset(turnstileWidgetId.current);
        }
        navigate('/contact-thank-you');
      },
      onRateLimit: () => {
        setError("Too many requests. Please try again later.");
      },
      onValidation: (message) => {
        setError(message);
        if (message.toLowerCase().includes('captcha')) {
          setTurnstileError("Verification failed. Please try again.");
        }
      }
    });

    // If in dev mode and we got a result, we're done
    if (DEV_MODE.isEnabled && devModeResult) {
      setLoading(false);
      return;
    }

    // Proceed with real API call
    const payload: ContactFormPayload = {
      name: data.name,
      email: data.email,
      phone: data.phone,
      message: data.message,
      captcha_token: getCaptchaToken(turnstileToken),
      website: data.website
    };

    try {
      console.log('Submitting form to API...');
      await submitContactForm(payload);
      console.log('Form submitted successfully');
      
      toast({
        title: "Message Sent",
        description: "Thank you for contacting us. We'll get back to you soon!",
      });
      form.reset();
      setTurnstileToken(null);
      if (turnstileWidgetId.current && !DEV_MODE.isEnabled) {
        window.turnstile.reset(turnstileWidgetId.current);
      }
      navigate('/contact-thank-you');
    } catch (err: any) {
      console.error('Form submission error:', err);
      if (err.status === 429) {
        setError("Too many requests. Please try again later.");
      } else if (err.status === 422) {
        setError(err?.message || "Validation failed. Please check your input and try again.");
        if (err?.message?.toLowerCase().includes('captcha')) {
          setTurnstileError("Verification failed. Please try again.");
        }
      } else {
        setError(err?.message || "Failed to send message. Please try again later.");
      }
      setTurnstileToken(null);
      if (turnstileWidgetId.current && !DEV_MODE.isEnabled) {
        window.turnstile.reset(turnstileWidgetId.current);
      }
    } finally {
      setLoading(false);
    }
  }

  // Add form validation logging
  React.useEffect(() => {
    const subscription = form.watch((value, { name, type }) => {
      console.log('Form field changed:', { name, type, value });
      console.log('Form errors:', form.formState.errors);
    });
    return () => subscription.unsubscribe();
  }, [form.watch, form.formState.errors]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-sauna-beige via-white to-sauna-beige/80 py-12 px-2 sm:px-6 lg:px-8 flex items-center justify-center">
      <Helmet>
        <title>Contact Us | SaunaDip</title>
        <meta name="description" content="Get in touch with the SaunaDip team. We're here to help with any questions about our sauna and cold plunge directory." />
      </Helmet>

      <div className="w-full max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-extrabold text-sauna-navy mb-2 tracking-tight drop-shadow-sm">Contact Us</h1>
          <p className="text-lg text-sauna-navy/70 mb-2">Have questions about SaunaDip? We're here to help!</p>
        </div>

        {/* Dev Mode Indicator - Only visible in development */}
        {DEV_MODE.isEnabled && (
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="px-3 py-1.5 bg-yellow-500/20 text-yellow-700 rounded-full text-sm font-medium shadow">Development Mode Active</div>
          </div>
        )}

        <Card className="border-none shadow-2xl rounded-2xl bg-white/90 backdrop-blur-md">
          <CardContent className="p-6 sm:p-10">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-7">
                {/* Honeypot field - hidden from real users */}
                <div style={{ display: 'none' }}>
                  <FormField
                    control={form.control}
                    name="website"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Website</FormLabel>
                        <FormControl>
                          <Input {...field} autoComplete="off" tabIndex={-1} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Your name" {...field} className="rounded-lg bg-sauna-beige/60 focus:bg-white focus:ring-2 focus:ring-sauna-orange transition" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input placeholder="Your email" type="email" {...field} className="rounded-lg bg-sauna-beige/60 focus:bg-white focus:ring-2 focus:ring-sauna-orange transition" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone (optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="Your phone number" type="tel" {...field} className="rounded-lg bg-sauna-beige/60 focus:bg-white focus:ring-2 focus:ring-sauna-orange transition" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Message</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Your message" className="min-h-[120px] rounded-lg bg-sauna-beige/60 focus:bg-white focus:ring-2 focus:ring-sauna-orange transition" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex flex-col items-center gap-3">
                  {/* Turnstile Widget Container - Only visible when not in dev mode */}
                  {!DEV_MODE.isEnabled && (
                    <div className="w-full flex justify-center">
                      <div id="turnstile-container" className="w-full max-w-xs rounded-lg border border-sauna-orange/30 bg-sauna-beige/40 p-2" />
                    </div>
                  )}
                  {turnstileError && (
                    <div className="text-red-600 text-sm font-medium flex items-center gap-1"><Mail size={16} />{turnstileError}</div>
                  )}
                  {!DEV_MODE.isEnabled && !turnstileToken && !turnstileError && (
                    <div className="text-yellow-600 text-sm font-medium flex items-center gap-1"><MessageSquare size={16} />Please complete the verification above.</div>
                  )}
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-sauna-orange to-sauna-navy text-white font-bold py-3 rounded-lg shadow-lg hover:from-sauna-navy hover:to-sauna-orange transition-all text-lg flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={loading || (!DEV_MODE.isEnabled && !turnstileToken)}
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size="sm" variant="default" />
                      <span className="ml-2">Sending...</span>
                    </>
                  ) : !DEV_MODE.isEnabled && !turnstileToken ? (
                    "Waiting for verification..."
                  ) : (
                    "Send Message"
                  )}
                </Button>
                {error && <div className="text-red-600 text-center mt-2 font-medium flex items-center justify-center gap-2"><Mail size={18} />{error}</div>}
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Contact;
